<template>
  <div class="sidekey-split">
    <!-- Left: existing settings UI (unchanged) -->
    <div class="pane pane-left">
      <SettingGroup title="サイド 1">
        <SettingItem label="ファンクション">
          <SelectBox
            :disabled="functionSelectDisable.side1"
            :options="selectboxOptions.side1.function"
            :defaultValue="String(form.selectbox.side1.function)"
            @update="handleSelectSide1Function"
          />
        </SettingItem>
        <SettingItem label="ホールド・ファンクション">
          <SelectBox
            :disabled="holdFunctionSelectDisable.side1"
            :options="selectboxOptions.side1.holdFunction"
            :defaultValue="String(form.selectbox.side1.holdFunction)"
            @update="handleSelectSide1HoldFunction"
          />
        </SettingItem>
      </SettingGroup>

      <SettingGroup title="サイド 2">
        <SettingItem label="ファンクション">
          <SelectBox
            :disabled="functionSelectDisable.side2"
            :options="selectboxOptions.side2.function"
            :defaultValue="String(form.selectbox.side2.function)"
            @update="handleSelectSide2Function"
          />
        </SettingItem>
        <SettingItem label="ホールド・ファンクション">
          <SelectBox
            :disabled="holdFunctionSelectDisable.side2"
            :options="selectboxOptions.side2.holdFunction"
            :defaultValue="String(form.selectbox.side2.holdFunction)"
            @update="handleSelectSide2HoldFunction"
          />
        </SettingItem>
      </SettingGroup>
    </div>

    <!-- Right: fixed guide image -->
    <div class="pane pane-right">
      <div class="photo-header">
        <div class="photo-title">サイドキー</div>
      </div>

      <div class="guide-wrap">
        <img class="guide-image" :src="sideGuideImage" alt="KBX-P51 side guide" />
      </div>
    </div>
  </div>
</template>

<script>
import SelectBox from '@/components/pc/radio/ipradio/atoms/SelectBox.vue'
import SettingGroup from '@/components/pc/radio/ipradio/organisms/layout/SettingGroup.vue'
import SettingItem from '@/components/pc/radio/ipradio/organisms/layout/SettingItem.vue'
import { BC, FUNCTION_LIST } from '@/components/pc/radio/ipradio/config/BCconstants'
import { useRadioSettingsStore } from '@/components/pc/radio/ipradio/stores/bcRadioSettings'
import { mapStores } from 'pinia'
import sideGuideImage from '@/assets/IP_Radio/KBX-P51_side_guide.png'

export default {
  name: 'SideKeyAssignmentVue2',
  components: { SelectBox, SettingGroup, SettingItem },
  data () {
    const baseOptions = (filterFn) =>
      FUNCTION_LIST.map(ele => {
        if (filterFn(ele)) return { label: ele.label, value: ele.value }
      }).filter(Boolean)

    const initialOptions = {
      side1: {
        function: baseOptions(ele => ele.type.key && ele.function && ele.function.function),
        holdFunction: baseOptions(ele => ele.type.key && ele.function && ele.function.holdFunction),
      },
      side2: {
        function: baseOptions(ele => ele.type.key && ele.function && ele.function.function),
        holdFunction: baseOptions(ele => ele.type.key && ele.function && ele.function.holdFunction),
      },
    }

    return {
      // UI選択肢
      selectboxOptions: initialOptions,

      // 右ペイン固定表示画像
      sideGuideImage,

      // フォーム
      form: {
        selectbox: {
          side1: {
            function: initialOptions.side1.function[0]?.label || 'None',
            holdFunction: initialOptions.side1.holdFunction[0]?.label || 'None',
          },
          side2: {
            function: initialOptions.side2.function[0]?.label || 'None',
            holdFunction: initialOptions.side2.holdFunction[0]?.label || 'None',
          },
        },
      },

      // ストアの一時設定
      setting: null,

      // 活性/非活性
      functionSelectDisable: { side1: false, side2: false },
      holdFunctionSelectDisable: { side1: false, side2: false },
    }
  },
  computed: {
    ...mapStores(useRadioSettingsStore),
  },
  created () {
    // ストアから設定取得
    this.setting = this.radioSettingsStore.getTempSetting()

    // 設定 → フォーム
    this.transSetting2Form()

    // 選択肢の初期再構成
    this.updateSelectboxOptions('side1')
    this.updateSelectboxOptions('side2')

    // AUXが有効なら side2 をロック
    const gen = this.setting?.KeyAssignment?.General
    if (gen?.Emergency?.AUX === BC.COMMON.TOGGLE.ENABLE) {
      this.functionSelectDisable.side2 = true
      this.holdFunctionSelectDisable.side2 = true
    }

    // SecondPTT ならホールド無効
    if (this.form.selectbox.side1.function === 'SecondPTT') {
      this.holdFunctionSelectDisable.side1 = true
    }
    if (this.form.selectbox.side2.function === 'SecondPTT') {
      this.holdFunctionSelectDisable.side2 = true
    }

    // フォーム変更を監視して 設定 ← フォーム & 保存
    this.$watch('form', () => {
      this.transForm2Setting()
      this.radioSettingsStore.saveTempSetting(this.setting)
    }, { deep: true })
  },
  methods: {
    deepCopy (obj) {
      try { return JSON.parse(JSON.stringify(obj)) } catch (_) { return {} }
    },

    // ------- 設定 ← フォーム -------
    transForm2Setting () {
      // console.log('============================')
      // console.log('before setting:', this.deepCopy(this.setting.KeyAssignment.SideKey))
      this.keyTransForm2Setting('side1')
      this.keyTransForm2Setting('side2')
      // console.log('after setting:', this.deepCopy(this.setting.KeyAssignment.SideKey))
    },
    keyTransForm2Setting (key) {
      const data = this.setting.KeyAssignment.SideKey
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1)

      const func = this.form.selectbox[key].function
      const hold = this.form.selectbox[key].holdFunction

      const isPressRelease = func === 'SecondPTT'
      const isPress =
        hold === 'None' ||
        (func === 'GroupDown' && hold === 'GroupDown(Continuous)') ||
        (func === 'GroupUp' && hold === 'GroupUp(Continuous)')

      data[capitalizedKey]['Function(Press)'] = (isPressRelease || isPress) ? func : 'None'
      data[capitalizedKey]['Function(Release)'] = (isPressRelease || !isPress) ? func : 'None'
      data[capitalizedKey].HoldFunction = hold
    },

    // ------- 設定 → フォーム -------
    transSetting2Form () {
      // console.log('============================')
      // console.log('before form:', this.deepCopy(this.form))
      this.keyTransSetting2Form('side1')
      this.keyTransSetting2Form('side2')
      // console.log('after form:', this.deepCopy(this.form))
    },
    keyTransSetting2Form (key) {
      const data = this.setting.KeyAssignment.SideKey
      const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1)

      const press = data[capitalizedKey]['Function(Press)']
      const hold = data[capitalizedKey].HoldFunction

      const isPress =
        hold === 'None' ||
        (press === 'GroupDown' && hold === 'GroupDown(Continuous)') ||
        (press === 'GroupUp' && hold === 'GroupUp(Continuous)')

      this.form.selectbox[key].function = isPress
        ? data[capitalizedKey]['Function(Press)']
        : data[capitalizedKey]['Function(Release)']

      this.form.selectbox[key].holdFunction = hold
    },

    // ------- 選択肢の再構成 -------
    updateSelectboxOptions (key) {
      const menuData = this.setting.KeyAssignment.Menu || []
      const extMenuData = this.setting.KeyAssignment.ExtendedMenu || []

      const hasMenuAutoBacklight =
        menuData.some(item => (item.Function || []).includes('AutoBacklight')) ||
        extMenuData.some(item => (item.Function || []).includes('AutoBacklight'))

      const formFunction = this.form.selectbox[key].function
      const formHoldFunction = this.form.selectbox[key].holdFunction
      const otherKey = key === 'side1' ? 'side2' : 'side1'
      const otherFormFunction = this.form.selectbox[otherKey].function

      // function
      this.selectboxOptions[key].function = FUNCTION_LIST.map(ele => {
        if (
          ele.type.key &&
          ele.function?.function &&
          !(hasMenuAutoBacklight && ele.value === 'Backlight') &&
          !(otherFormFunction === 'SecondPTT' && ele.value === 'SecondPTT')
        ) {
          return { label: ele.label, value: ele.value }
        }
      }).filter(Boolean)

      // holdFunction
      this.selectboxOptions[key].holdFunction = FUNCTION_LIST.map(ele => {
        if (
          (ele.type.key && ele.function?.holdFunction &&
            !(hasMenuAutoBacklight && ele.value === 'Backlight')) ||
          (formFunction === 'GroupDown' && ele.value === 'GroupDown(Continuous)') ||
          (formFunction === 'GroupUp' && ele.value === 'GroupUp(Continuous)') ||
          (formHoldFunction === 'Emergency' && ele.value === 'Emergency')
        ) {
          return { label: ele.label, value: ele.value }
        }
      }).filter(Boolean)
    },

    // ------- ハンドラ -------
    handleSelectFunction (key, value) {
      this.form.selectbox[key].function = value

      // デフォルトのホールド値に切替
      const holdDefault = FUNCTION_LIST.find(ele => ele.value === value)?.function?.holdFunctionDefault
      if (holdDefault) this.form.selectbox[key].holdFunction = holdDefault

      // SecondPTT 時はホールドを無効
      this.holdFunctionSelectDisable[key] = (this.form.selectbox[key].function === 'SecondPTT')

      // 選択肢を更新（対となるキーも）
      this.updateSelectboxOptions(key)
      this.updateSelectboxOptions(key === 'side1' ? 'side2' : 'side1')
    },
    handleSelectSide1Function (value) { this.handleSelectFunction('side1', value) },
    handleSelectSide1HoldFunction (value) { this.form.selectbox.side1.holdFunction = value },
    handleSelectSide2Function (value) { this.handleSelectFunction('side2', value) },
    handleSelectSide2HoldFunction (value) { this.form.selectbox.side2.holdFunction = value },
  },
}
</script>

<style scoped>
.sidekey-split {
  display: flex;
  gap: 16px;
  align-items: stretch;
}

.pane {
  min-width: 0;
}

.pane-left {
  flex: 1 1 60%;
}

.pane-right {
  flex: 1 1 40%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.photo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.photo-title {
  font-weight: 600;
}

.guide-wrap {
  margin-top: 12px;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.guide-image {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

@media (max-width: 900px) {
  .sidekey-split {
    flex-direction: column;
  }
}
</style>
